/**
 * Size Chart Modal Handler
 * Uses native HTML5 <dialog> .showModal() to appear in the browser's Top Layer
 * Conforms strictly to CRO guidelines in AGENTS.md:
 * - Pure functions and DOM event delegation
 * - Data attribute DOM access only
 * - Defensive try/catch wrapping
 * - Zero global variables
 */

(function () {
  'use strict';

  /**
   * Opens the size chart modal with specified image and title in the browser's Top Layer
   * @param {string} imageUrl
   * @param {string} title
   */
  function openSizeChart(imageUrl, title) {
    try {
      if (!imageUrl) return;

      const modal = /** @type {HTMLDialogElement | null} */ (document.querySelector('dialog[data-size-chart-modal]'));
      if (!modal) return;

      const imgElement = modal.querySelector('[data-size-chart-image]');
      const titleElement = modal.querySelector('[data-size-chart-title]');

      if (imgElement) {
        imgElement.src = imageUrl;
        imgElement.alt = title || 'Size Chart';
      }

      if (titleElement) {
        titleElement.textContent = title || 'Size Chart';
      }

      if (typeof modal.showModal === 'function') {
        if (!modal.open) {
          modal.showModal();
        }
      } else {
        modal.setAttribute('open', '');
      }

      document.body.classList.add('size-chart-open');
    } catch (error) {
      console.warn('[SizeChart] Open error:', error);
    }
  }

  /**
   * Closes the size chart modal
   */
  function closeSizeChart() {
    try {
      const modal = /** @type {HTMLDialogElement | null} */ (document.querySelector('dialog[data-size-chart-modal]'));
      if (!modal) return;

      if (typeof modal.close === 'function' && modal.open) {
        modal.close();
      } else {
        modal.removeAttribute('open');
      }

      document.body.classList.remove('size-chart-open');
    } catch (error) {
      console.warn('[SizeChart] Close error:', error);
    }
  }

  /**
   * Initializes delegated click and backdrop listeners for Size Chart
   */
  function initSizeChartEvents() {
    try {
      document.addEventListener('click', function (event) {
        try {
          const trigger = event.target.closest('[data-size-chart-trigger]');
          if (trigger) {
            event.preventDefault();
            event.stopPropagation();
            const imageUrl = trigger.getAttribute('data-size-chart-image') || '';
            const title = trigger.getAttribute('data-size-chart-title') || 'Size Chart';
            openSizeChart(imageUrl, title);
            return;
          }

          const closeBtn = event.target.closest('[data-size-chart-close]');
          if (closeBtn) {
            event.preventDefault();
            closeSizeChart();
            return;
          }

          // Click on dialog backdrop
          const modal = /** @type {HTMLDialogElement | null} */ (document.querySelector('dialog[data-size-chart-modal]'));
          if (modal && modal.open && event.target === modal) {
            closeSizeChart();
          }
        } catch (err) {
          console.warn('[SizeChart] Click delegation error:', err);
        }
      });

      document.addEventListener('keydown', function (event) {
        try {
          if (event.key === 'Escape' || event.key === 'Esc') {
            const modal = /** @type {HTMLDialogElement | null} */ (document.querySelector('dialog[data-size-chart-modal][open]'));
            if (modal) {
              closeSizeChart();
            }
          }
        } catch (err) {
          console.warn('[SizeChart] Keydown error:', err);
        }
      });

      const modal = document.querySelector('dialog[data-size-chart-modal]');
      if (modal) {
        modal.addEventListener('close', function () {
          document.body.classList.remove('size-chart-open');
        });
      }
    } catch (error) {
      console.warn('[SizeChart] Init error:', error);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSizeChartEvents);
  } else {
    initSizeChartEvents();
  }
})();
